import { repairLibraryClues } from './library-clue';
import type { Game } from './game';
import type { FloorId } from '@/world/types';
import { TILE } from '@/gfx/tiles';
import { ensureMemory, rebuildStatic, markSolid } from '@/world/builder';
import { ctxOf } from '@/engine/canvas';
import type { CharState, FollowMode, Character, Monster } from './entities';
import { makeMonster } from './entities';
import type { InvItem } from './items';
import { applyRunConfig, type RunConfig } from './run';
import { encodeMemoryAlpha, decodeMemoryAlpha } from './memory-codec';
import { isPaper, migratePapers, type JournalEntry } from './journal';
import { repairLooseItems } from './item-placement';

const KEY = 'homehomehome.save.v1';

interface SaveData {
  version?: number;
  rngState?: number;
  seed: number;
  cfg: RunConfig;
  time: number;
  flags: Record<string, boolean | number | string>;
  journal?: JournalEntry[];
  leaderId: string;
  selectedSlot?: number;
  entered: string[];
  chars: Array<{ id: string; floor: FloorId; x: number; y: number; state: CharState; mode: FollowMode; hideProp: string | null; battery: number; flashOn: boolean; inventory: InvItem[]; prisonRoom: string | null } & Partial<Pick<Character, 'facing' | 'flashAngle' | 'groggy' | 'hideReturn'>>>;
  monster: { floor: FloorId; x: number; y: number; carrying: string[]; speedMul: number } & Partial<Monster>;
  items: Game['items'];
  doors: Record<string, Array<{ id: string; open: boolean; locked: boolean; revealed: boolean }>>;
  props: Record<string, Array<{ id: string; x: number; y: number; searched: boolean; pushed: number; occupants: string[]; data: Record<string, unknown> }>>;
  memory: Record<string, string>;
  runs: number;
  captures: number;
}

export function hasSave(): boolean {
  try {
    return !!localStorage.getItem(KEY);
  } catch {
    return false;
  }
}
export function clearSave(): void {
  try {
    localStorage.removeItem(KEY);
  } catch { /* ignore */ }
}

export function saveGame(g: Game): void {
  const data: SaveData = {
    version: 2,
    rngState: g.rng.state,
    seed: g.seed,
    cfg: g.cfg,
    time: g.time,
    flags: g.flags,
    journal: g.journal,
    leaderId: g.leaderId,
    selectedSlot: g.selectedSlot,
    entered: g.entered,
    chars: [...g.chars.values()].map((c) => ({ id: c.id, floor: c.floor, x: c.x, y: c.y, state: c.state, mode: c.mode, hideProp: c.hideProp, battery: c.battery, flashOn: c.flashOn, inventory: c.inventory, prisonRoom: c.prisonRoom,
      facing: c.facing, flashAngle: c.flashAngle, groggy: c.groggy, hideReturn: c.hideReturn })),
    monster: { ...g.monster, path: null, pathTimer: 0, passagePlan: null, passageCheck: 0, searchPlan: null, searchStep: null, seenAt: null },
    items: g.items,
    doors: {},
    props: {},
    memory: {},
    runs: g.runs,
    captures: g.captures,
  };
  for (const f of g.mansion.floors.values()) {
    data.doors[f.id] = f.doors.map((d) => ({ id: d.id, open: d.open, locked: d.locked, revealed: d.revealed }));
    data.props[f.id] = f.props.filter((p) => p.searched || p.pushed || p.occupants.length || Object.keys(p.data).length).map((p) => ({ id: p.id, x: p.x, y: p.y, searched: p.searched, pushed: p.pushed, occupants: p.occupants, data: p.data }));
    if (f.memory) data.memory[f.id] = packMemory(f.memory);
  }
  try {
    localStorage.setItem(KEY, JSON.stringify(data));
  } catch (e) {
    console.warn('save failed', e);
  }
}

export function loadInto(g: Game): boolean {
  let data: SaveData;
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return false;
    data = JSON.parse(raw);
  } catch {
    return false;
  }
  if (!data || data.seed !== g.seed || !data.cfg || !Array.isArray(data.chars) || !data.monster ||
      !Array.isArray(data.items) || !data.doors || !data.props || !data.memory || (data.version ?? 1) > 2 ||
      data.journal !== undefined && (!Array.isArray(data.journal) || data.journal.some(e => !e || !isPaper(e.kind) || typeof e.text !== 'string')) ||
      !g.mansion.floors.has(data.monster.floor) || !Array.isArray(data.monster.carrying) ||
      !Number.isFinite(data.monster.x) || !Number.isFinite(data.monster.y) ||
      !g.chars.has(data.leaderId) || !data.chars.some(c => c.id === data.leaderId) ||
      data.chars.some(c => !g.mansion.floors.has(c.floor) || !Number.isFinite(c.x) || !Number.isFinite(c.y) || !Array.isArray(c.inventory)) ||
      !Array.isArray(data.cfg.prisons) || !data.cfg.prisons.length ||
      data.cfg.prisons.some(p => !g.mansion.floors.get(p.floor)?.roomById.has(p.room))) return false;
  g.cfg = data.cfg;
  applyRunConfig(g, g.cfg);
  g.time = data.time;
  g.flags = data.flags;
  g.journal = (data.journal ?? []).map(entry => ({ ...entry }));
  g.entered = data.entered;
  g.runs = data.runs;
  g.captures = data.captures;
  if (Number.isFinite(data.rngState)) g.rng.restore(data.rngState!);
  for (const sc of data.chars) {
    const c = g.chars.get(sc.id);
    if (!c) continue;
    Object.assign(c, { floor: sc.floor, x: sc.x, y: sc.y, state: sc.state, mode: sc.mode, hideProp: sc.hideProp, battery: sc.battery, flashOn: sc.flashOn, inventory: sc.inventory, prisonRoom: sc.prisonRoom });
    c.facing = sc.facing ?? c.facing;
    c.flashAngle = sc.flashAngle ?? c.flashAngle;
    c.groggy = sc.groggy ?? (c.state === 'groggy' ? 1.5 : 0);
    c.hideReturn = sc.hideReturn ?? null;
  }
  g.leaderId = data.leaderId;
  Object.assign(g.monster, makeMonster(), data.monster, { path: null, pathTimer: 0, passagePlan: null, passageCheck: 0, searchPlan: null, searchStep: null, seenAt: null });
  g.monster.carrying = [...new Set(data.monster.carrying)].filter(id => g.chars.has(id)).slice(0, 2);
  // Old saves lacked the state machine, but did record who was being carried.
  if (!data.monster.state) g.monster.state = g.monster.carrying.length ? 'carry' : 'patrol';
  for (const id of g.monster.carrying) {
    const c = g.chars.get(id)!;
    c.state = 'carried'; c.floor = g.monster.floor; c.x = g.monster.x; c.y = g.monster.y;
  }
  g.items = data.items;
  migratePapers(g);
  // Old saves have no selection. Invalid UI state must not prevent recovery of a run.
  g.selectedSlot = Number.isInteger(data.selectedSlot) && data.selectedSlot! >= 0 && data.selectedSlot! < g.slotCount(g.leader)
    ? data.selectedSlot! : 0;
  for (const f of g.mansion.floors.values()) {
    for (const sd of data.doors[f.id] ?? []) {
      const d = f.doors.find((d) => d.id === sd.id);
      if (d) Object.assign(d, sd);
    }
    for (const sp of data.props[f.id] ?? []) {
      const p = f.props.find((p) => p.id === sp.id);
      if (!p) continue;
      markSolid(f, p, false);
      const propData = { ...p.data, ...sp.data };
      if (p.kind.push && !Number.isFinite(sp.data?.originX)) propData.originX = p.data.legacyOriginX ?? p.data.originX ?? sp.x;
      Object.assign(p, sp, { data: propData });
      if (p.kind.push) p.pushed = Math.abs(p.x - Number(p.data.originX));
      markSolid(f, p, true);
    }
    rebuildStatic(f);
    const mem = data.memory[f.id];
    if (mem) unpackMemory(ensureMemory(f), mem, f.w, f.h);
  }
  repairLooseItems(g.mansion, g.items);
  repairLibraryClues(g);
  return true;
}

/** Keep exact alpha: tile-centre snapshots can reveal pixels behind an unseen wall. */
function packMemory(mem: HTMLCanvasElement): string {
  const ctx = ctxOf(mem);
  const img = ctx.getImageData(0, 0, mem.width, mem.height).data;
  return encodeMemoryAlpha(img);
}
function unpackMemory(mem: HTMLCanvasElement, packed: string, w: number, h: number): void {
  const ctx = ctxOf(mem);
  if (packed.startsWith('alpha1:')) {
    const rgba = decodeMemoryAlpha(packed, mem.width * mem.height);
    if (rgba) ctx.putImageData(new ImageData(rgba, mem.width, mem.height), 0, 0);
    return;
  }
  // Legacy tile masks cannot recover their original edge, but must not grow on each load.
  let s: string;
  try { s = atob(packed); } catch { return; }
  ctx.clearRect(0, 0, mem.width, mem.height);
  ctx.fillStyle = '#fff';
  for (let ty = 0; ty < h; ty++)
    for (let tx = 0; tx < w; tx++) {
      const i = ty * w + tx;
      if (s.charCodeAt(i >> 3) & (1 << (i & 7))) ctx.fillRect(tx * TILE, ty * TILE, TILE, TILE);
    }
}
